// Filtros, orden y derivados de Pagos recibidos. Todo puro: recibe filas y el día de hoy, devuelve
// filas o números. Nada de fechas implícitas — el «hoy» siempre entra por parámetro.
import dayjs from 'dayjs';
import { mxDay } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { plural } from '@/shared/lib/format/plural';
import type { StatsBarItem, StatsBarTone } from '@/shared/patterns/StatsBar';
import {
    ANY_CHANNEL,
    PAYMENT_STAT_KEYS,
    type CollectorPaymentRow,
    type PaymentChannel,
    type PaymentFilters,
    type PaymentStatKey,
    type PaymentStats,
    type PaymentTotals,
} from './types';

const MX_TIME_ZONE = 'America/Mexico_City';

/** La meta mensual del cobrador (`META_MES` del mock). Sin fuente: es constante del diseño. */
export const MONTHLY_GOAL = 180_000;

/** El sufijo de la celda Meta mes, con la cifra escrita tal cual la pinta el mock. */
export const MONTHLY_GOAL_LABEL = 'de $180K';

/** Los rótulos de la tabla, en el orden del mock. El candado de columnas compara contra esto. */
export const PAYMENT_COLUMN_LABELS = [
    'PG',
    'Cliente',
    'Fecha · hora',
    'Monto',
    'Canal',
    'CR',
] as const;

/** Los grupos de la columna de filtros, en el orden del mock. */
export const PAYMENT_FILTER_GROUPS = ['Búsqueda', 'Canal', 'Fecha', 'Monto'] as const;

/** Las opciones del combo Canal, con la redacción del mock. */
export const PAYMENT_CHANNEL_OPTIONS: readonly {
    value: PaymentChannel | typeof ANY_CHANNEL;
    label: string;
}[] = [
    { value: ANY_CHANNEL, label: 'Todos' },
    { value: 'store', label: 'Tienda' },
    { value: 'oxxo', label: 'OXXO' },
    { value: 'app', label: 'App Fiado' },
    { value: 'pcf-auto', label: 'PCF automático' },
];

/** Los cuatro atajos de fecha del mock, con cuántos días atrás mueve cada uno. */
export const PAYMENT_DATE_SHORTCUTS: readonly { label: string; days: number }[] = [
    { label: 'Hoy', days: 0 },
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: 'Todo', days: -1 },
];

export const PAYMENT_STAT_LABEL: Record<PaymentStatKey, string> = {
    all: 'Todos',
    today: 'Hoy',
    yesterday: 'Ayer',
    week: 'Semana',
    withPromise: 'Con compromiso',
};

/** El color con el que el mock destaca el número de cada pastilla. */
export const PAYMENT_STAT_TONE: Record<PaymentStatKey, StatsBarTone> = {
    all: 'neutral',
    today: 'success',
    yesterday: 'neutral',
    week: 'neutral',
    withPromise: 'info',
};

/** Las 5 pastillas de Pagos para la `<StatsBar>`, en el orden del mock. */
export function paymentsStatsItems(stats: PaymentStats): StatsBarItem<PaymentStatKey>[] {
    return PAYMENT_STAT_KEYS.map((key) => ({
        key,
        label: PAYMENT_STAT_LABEL[key],
        value: stats[key],
        tone: PAYMENT_STAT_TONE[key],
    }));
}

/** Cómo se pinta el canal en la celda y en el detalle (`chipCanal` del mock). */
export const PAYMENT_CHANNEL_CHIP: Record<
    PaymentChannel,
    { label: string; variant: 'info' | 'warning' | 'muted' }
> = {
    store: { label: 'Tienda', variant: 'info' },
    oxxo: { label: 'OXXO', variant: 'warning' },
    app: { label: 'App', variant: 'muted' },
    'pcf-auto': { label: 'PCF', variant: 'muted' },
};

export const EMPTY_PAYMENTS_TEXT = 'Sin pagos que coincidan con los filtros.';

export const NO_PROMISE_TEXT = 'Sin compromiso previo';

export const NO_CONTACT_HISTORY_TEXT = 'Sin historial de contacto.';

/** Cuántas líneas del historial entran en el bloque «Contexto». */
export const CONTACT_HISTORY_LIMIT = 4;

/** El día de calendario corrido `delta` días. Opera sobre días, no sobre instantes. */
export function shiftDay(day: string, delta: number): string {
    const moved = new Date(`${day}T00:00:00Z`);
    moved.setUTCDate(moved.getUTCDate() + delta);
    return moved.toISOString().slice(0, 10);
}

/** El lunes de la semana a la que pertenece ese día (`inicioSemana` del mock; domingo cierra). */
export function weekStartDay(day: string): string {
    const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
    return shiftDay(day, weekday === 0 ? -6 : 1 - weekday);
}

/** El día del piso de venta en que entró el pago. */
export function paymentDay(row: CollectorPaymentRow): string {
    return mxDay(row.receivedAt);
}

/** «06 jul · 09:15» — la celda Fecha · hora y la fila Fecha del detalle. */
export function formatPaymentDateTime(iso: string): string {
    return dayjs(iso).tz(MX_TIME_ZONE).format('DD MMM [·] HH:mm');
}

/** «06 jul 26» para un DÍA suelto: convertirlo de huso lo correría un día hacia atrás. */
export function formatPaymentDay(day: string): string {
    return dayjs(day).format('DD MMM YY');
}

export function formatPaymentAmount(amount: number): string {
    return formatMXN(amount);
}

/** La pastilla elegida recorta el universo antes que los filtros de la columna izquierda. */
export function applyPaymentStat(
    rows: readonly CollectorPaymentRow[],
    stat: PaymentStatKey,
    today: string,
): CollectorPaymentRow[] {
    if (stat === 'all') return [...rows];
    if (stat === 'withPromise') return rows.filter((row) => row.promise !== null);
    if (stat === 'today') return rows.filter((row) => paymentDay(row) === today);
    if (stat === 'yesterday') return rows.filter((row) => paymentDay(row) === shiftDay(today, -1));
    const monday = weekStartDay(today);
    return rows.filter((row) => paymentDay(row) >= monday);
}

/** La búsqueda mira cliente, crédito y folio, como el mock. */
function matchesSearch(row: CollectorPaymentRow, search: string): boolean {
    if (search === '') return true;
    const haystack = `${row.customerName} ${row.creditId} ${row.paymentId}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
}

export function filterPayments(
    rows: readonly CollectorPaymentRow[],
    filters: PaymentFilters,
): CollectorPaymentRow[] {
    return rows.filter((row) => {
        const day = paymentDay(row);
        if (filters.channel !== ANY_CHANNEL && row.channel !== filters.channel) return false;
        if (filters.fromDay !== '' && day < filters.fromDay) return false;
        if (filters.toDay !== '' && day > filters.toDay) return false;
        if (filters.amountMin !== null && row.amount < filters.amountMin) return false;
        if (filters.amountMax !== null && row.amount > filters.amountMax) return false;
        return matchesSearch(row, filters.search);
    });
}

/** El pago más reciente arriba: es el que el cobrador acaba de recibir. */
export function sortPaymentsByRecency(rows: readonly CollectorPaymentRow[]): CollectorPaymentRow[] {
    return [...rows].sort((left, right) => right.receivedAt.localeCompare(left.receivedAt));
}

export function sumPayments(rows: readonly CollectorPaymentRow[]): number {
    return rows.reduce((total, row) => total + row.amount, 0);
}

/** Los conteos de las pastillas: cuentan sobre TODO lo recibido, no sobre lo filtrado. */
export function computePaymentStats(
    rows: readonly CollectorPaymentRow[],
    today: string,
): PaymentStats {
    const stats = {} as PaymentStats;
    for (const key of PAYMENT_STAT_KEYS) {
        stats[key] = applyPaymentStat(rows, key, today).length;
    }
    return stats;
}

/** Cuántos filtros están puestos, para el conteo del encabezado de la columna. */
export function countActiveFilters(filters: PaymentFilters): number {
    let count = 0;
    if (filters.search.trim() !== '') count += 1;
    if (filters.channel !== ANY_CHANNEL) count += 1;
    if (filters.fromDay !== '') count += 1;
    if (filters.toDay !== '') count += 1;
    if (filters.amountMin !== null) count += 1;
    if (filters.amountMax !== null) count += 1;
    return count;
}

/** Cuántos días atrás quedó ese día, o `null` si el filtro es «Todo». */
export function dateShortcutRange(days: number, today: string): { fromDay: string; toDay: string } {
    if (days < 0) return { fromDay: '', toDay: '' };
    return { fromDay: shiftDay(today, -days), toDay: today };
}

export function computePaymentTotals(
    rows: readonly CollectorPaymentRow[],
    today: string,
    goal: number = MONTHLY_GOAL,
): PaymentTotals {
    const monday = weekStartDay(today);
    const month = today.slice(0, 7);
    const todayRows = rows.filter((row) => paymentDay(row) === today);
    const weekRows = rows.filter((row) => paymentDay(row) >= monday);
    const monthRows = rows.filter((row) => paymentDay(row).slice(0, 7) === month);
    const monthAmount = sumPayments(monthRows);
    return {
        todayAmount: sumPayments(todayRows),
        todayCount: todayRows.length,
        weekAmount: sumPayments(weekRows),
        weekCount: weekRows.length,
        monthAmount,
        monthCount: monthRows.length,
        goalPercent: goal > 0 ? Math.round((monthAmount / goal) * 100) : 0,
        averageTicket: monthRows.length === 0 ? 0 : Math.round(monthAmount / monthRows.length),
    };
}

/** «4 pagos» / «1 pago» — el contador de la barra de la tabla y el sub de cada KPI. */
export function paymentCountLabel(count: number): string {
    return plural(count, 'pago', 'pagos');
}
